import { ArrowBack, NavigateNext, PostAdd } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Grid,
  Pagination,
  PaginationItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { css, StyleSheet } from "aphrodite";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FilterCentroCusto } from "../../../components/filter/FilterCentroCusto";
import CustomDatagrid from "../../../components/datagrid/CustomDatagrid";
import Colors from "../../../components/constants/Colors";
import { usePaginationQuery } from "../../../helpers/pagination";
import { headers } from "../../../components/core/store/localStorage";
import axios from "axios";
import errorHandler from "../../../components/core/store/errorHandler";
import { validatePayloadDynamic } from "../../../helpers/errors";
import ErrorModal from "../../../components/modals/ErrorModal";
import { ConfirmModal } from "../../../components/modals/ConfirmModal";

const currentUser = sessionStorage.getItem("userName");
const currentEmp = sessionStorage.getItem("empName");
const API_URL = sessionStorage.getItem("apiUrl");
const PAGE_SIZE = 30;

export const CentroCusto = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { setQueryParams } = usePaginationQuery();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const page = Number(query.get("page") || 1);
  const filter = query.get("filtro");
  const isMobile = useMediaQuery("(max-width: 375px)");

  const [head, setHead] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [totalPages, setTotalPages] = useState(0);

  const [isSearch, setIsSearch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  const [titleLoading, setTitleLoading] = useState("Carregando...");
  const [codCentroCusto, setCodCentroCusto] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [centroCusto, setCentroCusto] = useState("");
  const [inativo, setInativo] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [messageModal, setMessageModal] = useState("");

  const [hasSearched, setHasSearched] = useState(false);
  const [openPayloadModal, setOpenPayloadModal] = useState(false);
  const [payloadErrors, setPayloadErrors] = useState([]);
  const [errors, setErrors] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const centroRef = useRef(null);

  useEffect(() => {
    if (API_URL === null) window.location.href = "/";
  }, []);

  useEffect(() => {
    if (!hasSearched) return;
    fetchCentroCusto(page, filter);
  }, [page, filter, hasSearched]);

  const fetchCentroCusto = async (pageToFetch) => {
    setLoading(true);
    setNotFound(false);

    try {
      const url = searchValue?.trim()
        ? `${API_URL}/centrocusto?page=${pageToFetch}&filtro=${encodeURIComponent(
            searchValue.trim(),
          )}`
        : `${API_URL}/centrocusto?page=${pageToFetch}`;

      const response = await axios.get(url, headers());

      const payload = response?.data || {};

      const data = payload.data || [];
      const pages = Number(payload.totalPages || 1);

      setTotalPages(pages);

      if (pageToFetch > pages) {
        setQueryParams({ page: pages }, true);
        return;
      }

      if (data.length === 0) {
        setNotFound(true);
        setShowGrid(false);
        if (pageToFetch > 1) setQueryParams({ page: 1 }, true);
        return;
      }

      setShowGrid(true);
      setHead(payload.columns || []);
      setRows(data);
      setTotals(payload.totalizador || {});
      setNotFound(false);
    } catch (error) {
      setShowGrid(false);
      console.log(error);
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setIsEdit(true);
    setIsSearch(false);
    setCodCentroCusto(row.id);
    setCentroCusto(row.name);
    setInativo(row.inativo === "Cancelado" ? true : false);
    setShowGrid(false);
    setErrors({});
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setOpenConfirmModal(true);

    row.inativo === "Cancelado"
      ? setMessageModal("Deseja realmente ativar este centro de custo?")
      : setMessageModal("Deseja realmente desativar este centro de custo?");
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setShowGrid(false);

    let novoStatusInativo;

    selectedRow.inativo === "Cancelado"
      ? (novoStatusInativo = false)
      : (novoStatusInativo = true);

    const payload = {
      id: selectedRow.id,
      name: selectedRow.name,
      inativo: novoStatusInativo,
    };

    // console.log(payload);

    try {
      await axios.post(`${API_URL}/centrocusto`, payload, headers());
      fetchCentroCusto(page);
    } catch (err) {
      errorHandler(err);
      setPayloadErrors([err.response.data.message]);
      setOpenPayloadModal(true);
    } finally {
      setLoading(false);
      setShowGrid(true);
      setOpenConfirmModal(false);
    }
  };

  const searchCentroCusto = () => {
    setHasSearched(true);
    setQueryParams({ page: 1, filtro: searchValue }, false);
  };

  const newCentroCusto = () => {
    setLoading(false);
    setIsSearch(false);
    setNotFound(false);
    setShowGrid(false);
    setInativo(false);
    setCodCentroCusto("");
    setSearchValue("");
    setHasSearched(false);
    setTotalPages(0);
    setRows([]);
    setHead([]);
  };

  const cadCentroCusto = async () => {
    const payload = {
      id: codCentroCusto,
      name: centroCusto.trim(),
      inativo,
    };

    const required = (cond, msg, fieldName, ref) => {
      if (cond) return false;
      setPayloadErrors([msg]);
      setOpenPayloadModal(true);
      setErrors((prev) => ({ ...prev, [fieldName]: true }));
      ref.current?.focus();
      return true;
    };

    if (
      required(
        !!centroCusto,
        "Centro de Custo é obrigatório.",
        "centroCusto",
        centroRef,
      )
    )
      return;

    try {
      setLoading(true);
      await axios.post(`${API_URL}/centrocusto`, payload, headers());
      setOpenPayloadModal(false);
      setCentroCusto("");
      setInativo(false);
      if (isEdit) {
        setIsEdit(false);
        handleBack();
      } else {
        setCodCentroCusto("");
      }
    } catch (error) {
      errorHandler(error);
      setPayloadErrors([error.response.data.message]);
      setOpenPayloadModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setErrors({});
    setIsSearch(true);
    setNotFound(false);
    setLoading(false);
    setCentroCusto("");
    setShowGrid(false);
    setHasSearched(false);
    setCodCentroCusto("");
    setSearchValue("");
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Grid wrap="wrap">
          <Grid
            size={{ xs: 6, sm: 7, md: 8, xl: 10 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Breadcrumbs
                separator={<NavigateNext fontSize="small" />}
                aria-label="breadcrumb"
              >
                <Typography
                  className={css(styles.pageTitle)}
                  sx={{ fontWeight: 400, color: "#828282" }}
                >
                  Contas a Pagar
                </Typography>
                <Typography className={css(styles.pageTitle)}>
                  Cadastros
                </Typography>
                <Typography className={css(styles.pageTitle)}>
                  Centro de Custos
                </Typography>
              </Breadcrumbs>
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              {isSearch && (
                <button
                  onClick={newCentroCusto}
                  className={css(styles.topBtn)}
                  title="Inclui"
                >
                  <PostAdd style={{ fontSize: "1.4rem" }} />
                </button>
              )}
              {!isSearch && (
                <button
                  onClick={handleBack}
                  className={css(styles.topBtn)}
                  title="Voltar"
                >
                  <ArrowBack style={{ fontSize: "1.4rem" }} />
                </button>
              )}
            </div>
          </Grid>
        </Grid>

        <hr
          style={{
            color: "#eee",
            // marginTop: "-0.55rem",
            marginBottom: "0.75rem",
          }}
        />

        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <FilterCentroCusto
            isSearch={isSearch}
            searchCentroCusto={searchCentroCusto}
            handleBack={handleBack}
            setErrors={setErrors}
            errors={errors}
            centroRef={centroRef}
            codCentroCusto={codCentroCusto}
            setCodCentroCusto={setCodCentroCusto}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            centroCusto={centroCusto}
            setCentroCusto={setCentroCusto}
            cadCentroCusto={cadCentroCusto}
          />

          {showGrid ? (
            <>
              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                }}
              >
                <CustomDatagrid
                  head={head}
                  row={rows}
                  totals={totals}
                  loading={loading}
                  hasTotal={true}
                  // onEyeClick={(row) => console.log("ver", row)}
                  onEditClick={(row) => handleEdit(row)}
                  columnOverrides={{
                    id: { minWidth: 150, maxWidth: 250 },
                    name: { minWidth: 200 },
                  }}
                  onDeleteClick={(row) => handleDelete(row)}
                />
                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    count={totalPages}
                    showFirstButton
                    showLastButton
                    sx={{
                      mt: 1,
                      "& .MuiPaginationItem-root.Mui-selected": {
                        background: Colors.mainLayoutAtiveBackground,
                        color: "black",
                      },
                    }}
                    renderItem={(item) => (
                      <PaginationItem
                        {...item}
                        component={Link}
                        to={`/contas-a-pagar/centro-custo${
                          item.page
                            ? `?page=${item.page}${filter ? `&filtro=${filter}` : ""}`
                            : ""
                        }`}
                      />
                    )}
                  />
                )}
              </Box>
            </>
          ) : null}
        </Box>

        {loading ? (
          <Box
            width={"100%"}
            height={"45vh"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid
              item
              xs={12}
              md={12}
              textAlign={"center"}
              justifyContent={"center"}
              alignItems={"center"}
              bgcolor={"#cadccaff"}
              borderRadius={5}
              padding={5}
              color={Colors.noResultFont}
            >
              <div display={"flex"} style={{ marginTop: "-0.5rem" }}>
                <CircularProgress color="success" />
              </div>
              <div display={"flex"}>
                <p
                  style={{
                    color: "#4b4b4b",
                    fontSize: 14,
                    marginBottom: "-1rem",
                  }}
                >
                  {titleLoading}
                </p>
              </div>
            </Grid>
          </Box>
        ) : null}
        {notFound ? (
          <Box
            width={"100%"}
            height={"45vh"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid
              size={{ xs: 12, md: 12 }}
              height={{ md: "20%", xs: "50%" }}
              display={"inline-flex"}
              textAlign={"center"}
              justifyContent={"center"}
              alignItems={"center"}
              bgcolor={"lightyellow"}
              borderRadius={5}
              padding={5}
              color={Colors.noResultFont}
            >
              <p
                style={{
                  color: "#4b4b4b",
                  fontSize: 14,
                }}
              >
                Sua pesquisa não retornou nenhum resultado.
              </p>
            </Grid>
          </Box>
        ) : null}

        <ErrorModal
          open={openPayloadModal}
          onClose={() => setOpenPayloadModal(false)}
          errors={payloadErrors}
        />

        <ConfirmModal
          open={openConfirmModal}
          onClose={() => setOpenConfirmModal(false)}
          onConfirm={handleConfirmDelete}
          message={messageModal}
        />
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  pageTitle: {
    fontFamily: "Poppins",
    fontWeight: 600,
    color: "#4b4b4b",
    fontSize: 14,
    margin: 0,
  },
  head: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    lineHeight: 1.5,
  },

  btn: {
    backgroundColor: "#669966",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    textAlign: "center",
    textDecoration: "none",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#669966",
    borderRadius: 4,
    marginTop: "-0.5rem",
    cursor: "pointer",

    padding: "5px 8px 3px 8px",
    transitionDuration: "0.2s",

    ":hover": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
    },
    ":active": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
      opacity: 0.6,
    },
  },
  topBtn: {
    backgroundColor: "#669966",
    cursor: "pointer",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#669966",
    borderRadius: 6,
    display: "flex !important",
    justifyContent: "center !important",
    alignContent: "center !important",
    padding: "4px 8px",
    transitionDuration: "0.2s",
    ":hover": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
    },
    ":active": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
      opacity: 0.6,
    },
    "@media (max-width: 375px)": {
      fontSize: 10,
      padding: "0.3rem 0.5rem",
    },
  },
});
